import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ObservedValueOf, Subject } from 'rxjs';

import { Session } from './session.model';
import { TrainingSessionCompany } from './company';
import { Subscriber } from 'rxjs/src/Subscriber';

import { saveAs } from 'file-saver';
import { log } from 'util';

@Injectable()
export class SessionService {
    private studentsCountForOneSessionTimeout: any[] = [];

    constructor(private http: HttpClient) {}

    findAllLocations(): Observable<any[]> {
        return this.http.get<any[]>('/api/zenika-locations', {});
    }

    findAllLocationsFormatted(): Observable<any> {
        return this.findAllLocations().pipe(
            map((agencies) => {
                const result = {
                    currentAgency: '',
                    agencies: [],
                    agenciesKeys: [],
                };

                let firstAgency = true;

                agencies
                    .sort((agencyA, agencyB) => {
                        const idsOrder = {
                            _20: 1,
                            _25672: 2,
                            _1890: 3,
                            _13350: 4,
                            _165900: 5,
                            _4091: 6,
                            _55005: 7,
                        };
                        return (
                            idsOrder['_' + agencyA.id] -
                            idsOrder['_' + agencyB.id]
                        );
                    })
                    .map((agency) => {
                        if (firstAgency) {
                            result.currentAgency = agency.city.toUpperCase();
                            firstAgency = false;
                        }
                        if (agency.city.toUpperCase() === 'BRUGES') {
                            agency.city = 'BORDEAUX';
                        }
                        if (agency.city.toUpperCase() === 'PARIS') {
                            agency.rooms = agency.rooms.filter(
                                (room) =>
                                    [5210911, 13395, 13394].indexOf(room.id) ===
                                    -1
                            );
                        }
                        result.agencies[agency.city.toUpperCase()] = agency;
                        agency.label = agency.city.toUpperCase();
                    });
                Object.keys(result.agencies).map((key) => {
                    let regexParis = /paris/gi;
                    if (key.match(regexParis)) {
                        result.agenciesKeys[0]
                            ? result.agenciesKeys.push(result.agenciesKeys[0])
                            : null;
                        result.agenciesKeys[0] = key;
                    } else {
                        result.agenciesKeys.push(key);
                    }
                });

                return result;
            })
        );
    }

    findOne(id): Observable<any> {
        return this.http.get('/api/training-sessions/' + id, {});
    }

    getTrainersResponses(id): Observable<any> {
        return this.http.get('/api/sessions/' + id + '/trainers-proposals', {});
    }

    proposeTrainingToTrainers(id, trainersId): Observable<any> {
        return this.http.post(
            '/api/sessions/' + id + '/trainers-proposals',
            trainersId,
            {}
        );
    }

    getStudentsCountForOneSession(trainingSessionId) {
        return Observable.create((observer: Subscriber<any>) => {
            this.studentsCountForOneSessionTimeout.push(
                setTimeout(() => {
                    this.http
                        .get(
                            `/api/training-sessions/students-count/${trainingSessionId}`,
                            {}
                        )
                        .subscribe((result) => {
                            observer.next(result);
                            observer.complete();
                        });
                }, 300)
            );
        });
    }

    clearStudentsCountForOneSessionTimeout() {
        this.studentsCountForOneSessionTimeout.forEach((timeout) => {
            try {
                clearTimeout(timeout);
            } catch (e) {}
        });
        this.studentsCountForOneSessionTimeout.length = 0;
    }

    /**
     * Deprecated
     *
     * @param sessionId
     * @returns {Observable<any>}
     */
    getRegisteredStudents(sessionId): Observable<any> {
        return this.http.get(
            '/api/registrations/registered-students/' + sessionId,
            {}
        );
    }

    getTrainingSessionsByCompanyId(companyId): Observable<any> {
        return this.http.get(
            '/api/companies/' + companyId + '/training-sessions',
            {}
        );
    }

    getTrainingSessionRegistrations(sessionId): Observable<any> {
        return this.http.get(
            `/api/training-sessions/${sessionId}/companies/`,
            {}
        );
    }

    sendSelectedDocumentsByMail(route): Observable<any> {
        return this.http.get(route, {});
    }

    getRegistrationOptions(sessionId: number): Observable<any> {
        return this.http.get(
            '/api/training-sessions/' + sessionId + '/options',
            {}
        );
    }

    saveRegistrationOption(option): Observable<any> {
        return this.http.post('/api/registration-option/', option, {});
    }

    updateRegistrationOption(option): Observable<any> {
        return this.http.put(
            '/api/registration-option/' + option.id,
            option,
            {}
        );
    }

    deleteRegistrationOption(id: number): Observable<any> {
        return this.http.delete('/api/registration-option/' + id, {});
    }

    getTrainingSessionById(sessionId: number): Observable<any> {
        return this.http.get('/api/training-sessions/' + sessionId, {});
    }

    getBackofficeUsers(): Observable<any> {
        return this.http.get('/api/backoffice-users/', {});
    }

    searchBackofficeUsers(keyword: string): Observable<any> {
        return this.http.get(
            `/api/backoffice-users/search?keyword=${keyword}`,
            {}
        );
    }

    findAll(): Observable<Session[]> {
        return this.http.get<Session[]>('/api/training-sessions', {});
    }

    create(session, commercial, availabilityTable) {
        return this.http.post<any>('/api/training-sessions', {
            session,
            commercial,
            availabilityTable,
        });
    }

    update(session) {
        return this.http.put<any>('/api/training-sessions', session);
    }

    remove(currentSession: Session, notifyTrainer = true) {
        return this.http.delete(
            `/api/training-sessions/${currentSession.id}?notifyTrainer=${notifyTrainer}`
        );
    }

    removeRegistrationDetail(registrationDetailId: number): Observable<any> {
        return this.http.delete(
            '/api/registration-details/' + registrationDetailId,
            {}
        );
    }

    addCompanyToSession(
        sessionCompany: any,
        sessionId: number
    ): Observable<TrainingSessionCompany> {
        return this.http.post(
            '/api/training-sessions/' + sessionId + '/companies',
            sessionCompany
        );
    }

    updateCompanyToSession(
        sessionCompany: any,
        sessionId: number
    ): Observable<TrainingSessionCompany> {
        return this.http.put(
            '/api/training-sessions/' +
                sessionId +
                '/companies/' +
                sessionCompany.id,
            sessionCompany
        );
    }

    addStudentToCompanySession(sessionCompanyStudent: any, sessionId: number) {
        return this.http.post<any>(
            '/api/training-sessions/' +
                sessionId +
                '/companies/' +
                sessionCompanyStudent.companyId +
                '/students',
            sessionCompanyStudent
        );
    }

    updateStudentToCompanySession(
        sessionCompanyStudent: any,
        sessionId: number
    ) {
        return this.http.put(
            '/api/training-sessions/' +
                sessionId +
                '/companies/' +
                sessionCompanyStudent.companyId +
                '/students/' +
                sessionCompanyStudent.registrationDetailId,
            sessionCompanyStudent
        );
    }

    generateZevalAccessId(sessionId: number) {
        return this.http.post<any>(
            `/api/training-sessions/${sessionId}/zevalAccessId`,
            {}
        );
    }

    forceGoogleCalendarSync(sessionId: number) {
        return this.http
            .post(
                `/api/training-session/${sessionId}/force-google-calendar-sync`,
                {}
            )
            .pipe(map(() => undefined));
    }

    syncSignature(sessionId: number) {
        return this.http
            .post(`/api/training-session/${sessionId}/sync-signature`, {})
            .pipe(map(() => undefined));
    }

    async exportPerMonth(
        startDate: string,
        endDate: string,
        deleted?: boolean
    ) {
        const response = await this.http
            .get(
                '/api/training-sessions/export?from=' +
                    startDate +
                    '&to=' +
                    endDate +
                    '&deleted=' +
                    (deleted || false),
                { responseType: 'blob', observe: 'response' }
            )
            .toPromise();
        return this.saveToFileSystem(response);
    }

    sendBillingReminderEmailToTrainer(sessionId: number, body: string) {
        return this.http
            .post(
                `/api/training-sessions/sendBillingReminderEmailToTrainer/${sessionId}`,
                { body }
            )
            .pipe();
    }

    sendTrainingSessionRecapToContact(
        sessionId: number,
        contactId: number,
        body: string
    ) {
        return this.http
            .post(
                `/api/training-sessions/send-recap/sessions/${sessionId}/contacts/${contactId}`,
                { body }
            )
            .pipe();
    }

    private saveToFileSystem(response) {
        const contentDispositionHeader: string = response.headers.get(
            'Content-Disposition'
        );
        const parts: string[] = contentDispositionHeader.split(';');
        const filename = parts[1].split('=')[1];
        const blob = new Blob([response.body], {
            type: 'application/vnd.ms-excel',
        });
        saveAs(blob, filename);
    }

    sendSurveyByMail(traininSessionId: number, surveyType, students) {
        return this.http
            .post<any>(
                `/api/training-session/${traininSessionId}/send-survey`,
                {
                    surveyType,
                    students,
                }
            )
            .pipe();
    }

    getRedirectionToSurveyReport(sessionId) {
        this.http
            .get<{ surveyURL: string }>(
                `/api/training-session/${sessionId}/survey-monkey-url`,
                {}
            )
            .subscribe((res) => (window.location.href = res.surveyURL));
    }
}



// WEBPACK FOOTER //
// ./src/app/_services/session/session.service.ts